import {
    Alert,
    DataTable,
    Icon,
    Loading,
    SearchInput,
    Spacer,
    StyleFunction,
    Surface,
    useAuth,
    usePagination,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import { useBookkeepingAccountsQuery } from "graphql-sdk";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RootStackScreenProps } from "../../../navigation";
import { AdminContainer } from "../../container";
import { AccountRow } from "./account-row";

type ScreenProps = RootStackScreenProps<"BOOKKEEPING_ACCOUNTS">;

export function BookkeepingAccounts({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.accounts");

    const theme = useTheme();
    const [t] = useTranslation();
    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
    });

    const styles = useThemedStyle(styleFunc);
    const [search, setSearch] = useState<string>("");

    // GraphQL
    const { data, loading, error, refetch } = useBookkeepingAccountsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            search: {
                query: search,
            },
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (route.params?.refetch) {
            refetch();
        }
    }, [refetch, route.params?.refetch]);

    // Actions
    const handleView = useCallback(
        (id: string) => {
            navigate("BOOKKEEPING_ACCOUNT_VIEW", {
                id,
            });
        },
        [navigate]
    );

    const handleEdit = useCallback(
        (id: string) => {
            navigate("BOOKKEEPING_ACCOUNT_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const handleSearchTextChange = useCallback(
        text => {
            setSearch(text);
            // return to first page in list
            onPageChange(0);
        },
        [onPageChange]
    );

    return (
        <AdminContainer>
            <SearchInput onChange={handleSearchTextChange} />
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={sortBy === "name" && sortDirection}
                            onPress={() => onSortChange("name")}
                        >
                            {t("common.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "created_at" && sortDirection
                            }
                            onPress={() => onSortChange("created_at")}
                        >
                            {t("common.created_at", "Created at")}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {error ? (
                        <Alert type="error">
                            {t(
                                "backoffice.error.from_server",
                                "There was an error: {{errorText}}",
                                {
                                    errorText: error.message,
                                }
                            )}
                        </Alert>
                    ) : loading ? (
                        <>
                            <Spacer />
                            <Loading />
                            <Spacer />
                        </>
                    ) : (
                        data?.bookkeepingAccounts.data.map(item => (
                            <AccountRow
                                key={item.id}
                                item={item}
                                onSelect={() => handleView(item.id)}
                                onEdit={() => handleEdit(item.id)}
                            />
                        ))
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={
                            data?.bookkeepingAccounts.pagination.pages
                        }
                        itemCount={
                            data?.bookkeepingAccounts.pagination.resultCount
                        }
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        justifyContent: "space-between",
        marginBottom: theme.spacingScale * 2,
        flexWrap: "wrap",
    },
    actionButtonContainer: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    button: {
        alignSelf: "flex-end",
        marginLeft: theme.spacingScale,
    },
    iconColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 40 },
});
